import { useState, useRef, useEffect } from 'react';
import Form, { type RuleObject } from 'antd/es/form';
import { getCheckEmail } from 'queries';
import { parseErrors } from 'helpers';

export const useEmailValidation = (
  customerType: string,
  name: string,
  initialValue?: string,
  disableEmailCheck?: boolean,
  isRequired?: boolean,
) => {
  const [isFieldValid, setIsFieldValid] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>();
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const previousValueRef = useRef<string | undefined>(initialValue);
  const forceRevalidationRef = useRef<boolean>(false);
  const [isValidationEnabled, setIsValidationEnabled] = useState(false);
  const form = Form.useFormInstance();

  useEffect(() => {
    forceRevalidationRef.current = true;
    !!form?.getFieldValue(name) && form?.validateFields([name]);
  }, [customerType]);

  const validateEmail = async (_: RuleObject, value: string) => {
    if (!isRequired && !value) {
      Promise.resolve();
      return;
    }

    const shouldSkipValidation =
      (previousValueRef.current === value || value === initialValue || !isValidationEnabled) &&
      (!forceRevalidationRef.current || value === initialValue);

    if (shouldSkipValidation || disableEmailCheck) {
      if (isFieldValid === false && value !== initialValue) {
        return Promise.reject(new Error(errorMessage));
      }
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    return new Promise<void>((resolve, reject) => {
      debounceRef.current = setTimeout(async () => {
        try {
          const data = await getCheckEmail({ email: value, customerType });
          if (data.length === 0) {
            setIsFieldValid(true);
            setErrorMessage(undefined);
            previousValueRef.current = value;
            resolve();
          }
        } catch (error) {
          const errorMsg = parseErrors(error).join(', ');
          setErrorMessage(errorMsg);
          setIsFieldValid(false);
          reject(new Error(errorMsg));
        }
        forceRevalidationRef.current = false;
      }, 300);
    });
  };

  return {
    validateEmail,
    setIsValidationEnabled,
  };
};
